import { useQuery } from "@tanstack/react-query";
import { getWaffleFlags } from "../../http/endpoints";
import { find } from "lodash";
import { useMemo } from "react";

enum WaffleFlag {
  BNBMainnet = "bnb-mainnet",
  BNBTestnet = "bnb-testnet",
  GasManagerPredeposit = "gas-manager-predeposit",
  SignerService = "signer-service",
  UsageLimitBanner = "usage_limit_banner",
  UsageLimitBannerHoverState = "usage_limit_banner_hover_state",
  zkSync = "zksync",
  EthHolesky = "eth-holesky",
  Avax = "avax",
  ArbNova = "arb-nova",
}

const WAFFLE_FLAG_KEY = "featureFlags";

function useWaffleFlags<TFlag extends WaffleFlag>(
  featureFlags: TFlag[],
): Record<TFlag, boolean> {
  const { data: flagObjs } = useQuery({
    queryKey: [WAFFLE_FLAG_KEY, featureFlags],
    queryFn: () => getWaffleFlags({ flags: featureFlags }),
    staleTime: 60 * 1000,
  });

  return useMemo(
    () =>
      featureFlags.reduce((acc, flag) => {
        // Flags default to false if they can't be loaded
        acc[flag] = find(flagObjs || [], { name: flag })?.active ?? false;
        return acc;
      }, {} as Record<TFlag, boolean>),
    [featureFlags, flagObjs],
  );
}

function useWaffleFlag(featureFlag: WaffleFlag) {
  const flags = useWaffleFlags([featureFlag]);
  return flags[featureFlag];
}

export { useWaffleFlags, useWaffleFlag, WaffleFlag };
